import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Loader2, Baby, TrendingUp, AlertOctagon, Notebook } from "lucide-react";

function Bar({ label, value, max = 5, tone = "sage" }) {
  const pct = max ? Math.min(100, (value / max) * 100) : 0;
  const color = tone === "ochre" ? "bg-ochre-500" : "bg-sage-600";
  return (
    <div>
      <div className="flex justify-between text-xs"><span className="text-stone2-600">{label}</span><span className="text-sage-900 font-semibold">{value?.toFixed?.(1) ?? value ?? "—"}</span></div>
      <div className="mt-1 h-1.5 bg-white rounded-full overflow-hidden border border-stone2-200">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function InsightsPage() {
  const [children, setChildren] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);

  useEffect(() => {
    api.get("/children").then((r) => {
      setChildren(r.data);
      if (r.data.length > 0) setActiveId(r.data[0].id);
      else setLoading(false);
    }).catch((e) => { toast.error(extractError(e)); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!activeId) return;
    setLoading(true); setData(null); setGated(false);
    api.get(`/insights/child/${activeId}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e?.response?.status === 402) setGated(true);
        else toast.error(extractError(e));
      })
      .finally(() => setLoading(false));
  }, [activeId]);

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="insights-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <span className="section-eyebrow">Family Premium</span>
        <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900 flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-sage-600" /> Behavioral Insights
        </h1>
        <p className="text-stone2-600 mt-1 text-sm">Aggregated session notes, mood trends, and incident history per child.</p>

        {children.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {children.map((c) => (
              <button key={c.id} type="button" onClick={() => setActiveId(c.id)}
                data-testid={`insights-child-tab-${c.id}`}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors duration-200 ${
                  activeId === c.id ? "bg-sage-600 text-white border-sage-600" : "bg-white border-stone2-200 text-sage-800 hover:border-sage-400"
                }`}>
                <Baby className="w-3.5 h-3.5 inline mr-1" /> {c.name}
              </button>
            ))}
          </div>
        )}

        {loading && <div className="mt-8 text-stone2-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}

        {gated && (
          <div className="mt-8 paper-card p-8 text-center" data-testid="insights-gated">
            <BarChart3 className="w-10 h-10 mx-auto text-ochre-500" />
            <h2 className="mt-3 text-2xl font-heading font-bold text-sage-900">Family Premium feature</h2>
            <p className="text-stone2-600 mt-2">Upgrade to unlock behavioral insights per child.</p>
            <Link to="/pricing" className="btn-primary mt-4 inline-flex">See pricing</Link>
          </div>
        )}

        {data && (
          <>
            <div className="mt-8 grid sm:grid-cols-4 gap-4" data-testid="insights-counts">
              <div className="rounded-xl bg-sage-50 p-5"><div className="text-xs uppercase tracking-[0.18em] font-bold text-sage-700">Sessions logged</div><div className="text-3xl font-heading font-bold text-sage-900 mt-1">{data.counts.sessions_logged}</div></div>
              <div className="rounded-xl bg-sage-50 p-5"><div className="text-xs uppercase tracking-[0.18em] font-bold text-sage-700">Bookings</div><div className="text-3xl font-heading font-bold text-sage-900 mt-1">{data.counts.bookings}</div></div>
              <div className="rounded-xl bg-ochre-50 p-5"><div className="text-xs uppercase tracking-[0.18em] font-bold text-ochre-800">Incidents</div><div className="text-3xl font-heading font-bold text-sage-900 mt-1">{data.counts.incidents}</div></div>
              <div className="rounded-xl bg-sage-50 p-5"><div className="text-xs uppercase tracking-[0.18em] font-bold text-sage-700">Reviews</div><div className="text-3xl font-heading font-bold text-sage-900 mt-1">{data.counts.reviews}</div></div>
            </div>

            <section className="mt-6 grid md:grid-cols-2 gap-4">
              <div className="paper-card p-5">
                <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-sage-600" /><h3 className="font-heading font-semibold text-sage-900">Averages</h3></div>
                <div className="mt-3 space-y-3">
                  <Bar label="Mood (last sessions)" value={data.averages.mood_rating || 0} />
                  <Bar label="Review rating" value={data.averages.review_rating || 0} tone="ochre" />
                </div>
              </div>
              <div className="paper-card p-5">
                <div className="flex items-center gap-2"><AlertOctagon className="w-4 h-4 text-danger" /><h3 className="font-heading font-semibold text-sage-900">Incidents by severity</h3></div>
                <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
                  {["low","moderate","high","critical"].map((k) => (
                    <div key={k} className="rounded-lg bg-stone2-50 p-3">
                      <div className="text-2xl font-heading font-bold text-sage-900">{data.severity_counts[k]}</div>
                      <div className="text-stone2-500 capitalize">{k}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mt-6 paper-card p-5">
              <div className="flex items-center gap-2"><Notebook className="w-4 h-4 text-sage-600" /><h3 className="font-heading font-semibold text-sage-900">Recent session notes</h3></div>
              {data.recent_notes.length === 0 && <p className="text-sm text-stone2-500 mt-2">No notes yet.</p>}
              <ul className="mt-3 space-y-3">
                {data.recent_notes.map((n) => (
                  <li key={n.id} className="border-l-2 border-sage-300 pl-3">
                    <div className="text-xs text-stone2-500">{new Date(n.created_at).toLocaleString()}</div>
                    {n.successes && <div className="text-sm text-sage-900 mt-1"><span className="font-semibold">Successes:</span> {n.successes}</div>}
                    {n.challenges && <div className="text-sm text-stone2-700 mt-1"><span className="font-semibold">Challenges:</span> {n.challenges}</div>}
                    {n.noteworthy_events && <div className="text-sm text-ochre-800 mt-1"><span className="font-semibold">Noteworthy:</span> {n.noteworthy_events}</div>}
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
